import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DialogContent, Button } from "@mui/material";
import DynamicForm from "../../../common/DynamicForm/DynamicForm";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { RootState } from "../../../store/store";
import { IAsyncOptionFn, IGetOptionsResponse } from "../../../models/user/DynamicForm/DynamicForm";
import { CustomAction } from "../../../store/customAction";
import { API_GET_LOOKUP_VALUES } from "../../../Apis/ApiEndPoints";
import { PostAddDriver } from "../../../store/user/policyChange/policyChangeActions";
import { IDriverPolicyChangeRequestParams } from "../../../models/user/PolicyChange/PolicyChange";
import { GetAddDriverFormJson } from "../../../store/user/driver/driverActions";
import MessageDialog from "../../../components/MessageDialog/MessageDialog";
import { setCalledReducerType } from "../../../store/common/CommonSlice";
import { Api } from "../../..";
import { useTranslation } from "react-i18next";

export interface AddDriverPanelProps {
  policyId: string;
  carrier: string;
  lobCode: string;
  system: string;
  policyNumber?: string;
  onAction?: (action: { followup_message?: string; label?: string }) => void;
}

const AddDriverPanel: React.FC<AddDriverPanelProps> = ({ policyId, carrier, lobCode, system, policyNumber, onAction }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { userDetails } = useAppSelector((state: RootState) => state.userAuth);
  const { policyDetailsHeader } = useAppSelector((state: RootState) => state.policyDetails);
  const { addDriverFormFieldsJson } = useAppSelector((state: RootState) => state.driver);
  const { isSuccess, isError, calledReducerType } = useAppSelector((state: RootState) => state.common);

  const [messageDialogOpen, setMessageDialogOpen] = useState<boolean>(false);

  const asyncFunctions: { [key: string]: IAsyncOptionFn } = useMemo(
    () => ({
      getPrefixOptions: async (fieldName) => {
        try {
          const response: IGetOptionsResponse = await CustomAction(Api.get(`${API_GET_LOOKUP_VALUES}/${fieldName}`), dispatch, "getPrefixOptions");
          return response.data;
        } catch (error) {
          return [];
        }
      },
      getMaritalStatusOptions: async (fieldName) => {
        try {
          const response: IGetOptionsResponse = await CustomAction(Api.get(`${API_GET_LOOKUP_VALUES}/${fieldName}`), dispatch, "getMaritalStatusOptions");
          return response.data;
        } catch (error) {
          return [];
        }
      },
      getGenderOptions: async (fieldName) => {
        try {
          const response: IGetOptionsResponse = await CustomAction(Api.get(`${API_GET_LOOKUP_VALUES}/${fieldName}`), dispatch, "getGenderOptions");
          return response.data;
        } catch (error) {
          return [];
        }
      },
      getRelationshipToInsuredOptions: async (fieldName) => {
        try {
          const response: IGetOptionsResponse = await CustomAction(Api.get(`${API_GET_LOOKUP_VALUES}/${fieldName}`), dispatch, "getRelationshipToInsuredOptions");
          return response.data;
        } catch (error) {
          return [];
        }
      },
      getEmploymentStatusOptions: async (fieldName) => {
        try {
          const response: IGetOptionsResponse = await CustomAction(Api.get(`${API_GET_LOOKUP_VALUES}/${fieldName}`), dispatch, "getEmployementStatusOptions");
          return response.data;
        } catch (error) {
          return [];
        }
      },
      getEducationLevelOptions: async (fieldName) => {
        try {
          const response: IGetOptionsResponse = await CustomAction(Api.get(`${API_GET_LOOKUP_VALUES}/${fieldName}`), dispatch, "getEducationLevelOptions");
          return response.data;
        } catch (error) {
          return [];
        }
      },
      getLicensingStateOptions: async (fieldName) => {
        try {
          const response: IGetOptionsResponse = await CustomAction(Api.get(`${API_GET_LOOKUP_VALUES}/${fieldName}`), dispatch, "getLicensingStateOptions");
          return response.data;
        } catch (error) {
          return [];
        }
      },
      getDriverLicenseStatusOptions: async (fieldName) => {
        try {
          const response: IGetOptionsResponse = await CustomAction(Api.get(`${API_GET_LOOKUP_VALUES}/${fieldName}`), dispatch, "getDriverLicenseStatusOptions");
          return response.data;
        } catch (error) {
          return [];
        }
      },
    }),
    [dispatch]
  );

  const handleAddDriverFromOnSubmit = useCallback(
    (formValues: { [key: string]: any }) => {
      if (userDetails && userDetails.customerId && policyId && system && carrier && lobCode) {
        const resolvedCarrierPolicyNo: string | undefined = policyNumber ?? policyDetailsHeader?.carrierPolicyNumber;
        if (!resolvedCarrierPolicyNo) {
          return;
        }
        const requestParms: IDriverPolicyChangeRequestParams = {
          carrierPolicyNo: resolvedCarrierPolicyNo,
          changeDt: formValues.changeDt.format("YYYY-MM-DD"),
          customerId: userDetails.customerId,
          detailDescription: t("Add Driver to the Policy"),
          policyRef: policyId,
          system,
          carrier,
          lobCode,
          driverInfo: {
            dateOfBirth: formValues.dateOfBirth.format("YYYY-MM-DD"),
            driverAge: String(new Date().getFullYear() - formValues.dateOfBirth.year()),
            genderCd: formValues.genderCd,
            givenName: formValues.givenName,
            licensedStateProvCd: formValues.licensedStateProvCd,
            licenseDt: formValues.licenseDt.format("YYYY-MM-DD"),
            licenseNumber: formValues.licenseNumber,
            maritalStatus: formValues.maritalStatus,
            otherGivenName: formValues.otherGivenName,
            relationshipToInsuredCd: formValues.relationshipToInsuredCd,
            surName: formValues.surName,
          },
        };
        dispatch(PostAddDriver(requestParms));
      }
    },
    [userDetails, policyId, system, carrier, lobCode, dispatch, policyDetailsHeader, policyNumber, t]
  );

  const handleMessageDialogClose = useCallback(() => {
    setMessageDialogOpen(false);
    dispatch(setCalledReducerType(null));
  }, [dispatch]);

  const handleCancel = useCallback(() => {
    const pn = policyNumber ?? policyDetailsHeader?.carrierPolicyNumber;
    if (pn && onAction) {
      onAction({ followup_message: `Show me details for policy ${pn}` });
    }
  }, [onAction, policyNumber, policyDetailsHeader?.carrierPolicyNumber]);

  useEffect(() => {
    dispatch(GetAddDriverFormJson());
  }, [dispatch]);

  useEffect(() => {
    if ((isSuccess || isError) && calledReducerType === "policyChange/PostAddDriver") {
      setMessageDialogOpen(true);
      if (isSuccess) {
        const pn = policyNumber ?? policyDetailsHeader?.carrierPolicyNumber;
        if (pn && onAction) {
          onAction({ followup_message: `Show me details for policy ${pn}` });
        }
      }
    }
  }, [isSuccess, isError, calledReducerType]);

  return (
    <>
      <DialogContent>
        {addDriverFormFieldsJson ? (
          <DynamicForm
            formFieldsJson={addDriverFormFieldsJson}
            asyncOptionsFunctions={asyncFunctions}
            onFormSubmit={handleAddDriverFromOnSubmit}
            rightActions={
              <Button variant="contained" color="primary" onClick={handleCancel}>
                {t("Cancel")}
              </Button>
            }
          />
        ) : (
          <></>
        )}
      </DialogContent>
      <MessageDialog open={messageDialogOpen} handleClose={handleMessageDialogClose} />
    </>
  );
};

export default AddDriverPanel;


